import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { areFilterIdsEqual } from "utils/filterUtils";
import { buildClassificationPath } from "utils/productRoutes";

import FilterSection from "./FilterSection";

import { Icon } from "@/design-system";

/**
 * The rail's Category facet, on the reference's own model (grep `catFilters` and
 * `__catRow` in `Dens N Dente Tailwind.dc.html`): **single-select navigation, not
 * checkboxes.**
 *
 * - Every row is a link to that category's listing; "All products" sits on top. The
 *   reference's rows are `<button onClick={nav}>`; ours are `<Link>`s — the same call
 *   every ported nav surface has made (middle-click, Cmd-click).
 * - **Expansion follows the active category — there are no chevrons.** The category
 *   whose listing you are on (or whose subcategory's listing you are on) expands its
 *   subcategories in place; everything else stays a single row. This replaced an
 *   app-invented chevron-and-checkbox tree, on request.
 * - Row states are the reference's own classes: active =
 *   `bg-[var(--neutral-100)] text-orange-700 font-semibold`; the open-but-not-active
 *   parent = `text-ink font-semibold`; everything else `text-ink-2`. Subcategory rows
 *   add `!pl-[24px]` (the `!` beats the row's own `px-[8px]`). Non-active rows hover
 *   soft blue — the reference does that with a `[data-catfilter]:not(.catf-on):hover`
 *   CSS rule; conditional Tailwind classes express it here without the CSS bucket.
 * - `CAT_CAP = 8` parents show before the counted "Show N more" (reference value and
 *   copy — "Show fewer" when open). **The branch holding the active category is always
 *   included, even past the cap** — the reference's dated note: arriving from the
 *   mega-menu must never hide your position.
 * - The header × navigates to `/products`, exactly the reference's `clearFCat` →
 *   `nav('listing', null)`. The summary is the active node's own name — sub name when a
 *   sub is active (`(__catHit.sub || __catHit.cat).label`).
 * - No per-row counts: the reference counts a client-side fixture; there is no
 *   count-per-category endpoint (`dnd-backend/references/backlog.md`). Same divergence
 *   as the mega-menu and `/categories`.
 *
 * Navigation means the category choice lives in the **URL**, not in
 * `selectedCategories` — shareable, Back/Forward works, and the scoped listing loads
 * brand/manufacturer facets for that category. Multi-category selection via
 * `selectedCategories` still exists on brand listings' flat list, and the backend's
 * parent-id expansion is untouched.
 */
const CAT_CAP = 8;

const rowBase =
  "flex items-center gap-[var(--space-2)] rounded-sm px-[8px] py-[6px] text-left font-sans text-xs [transition:background_var(--duration-fast),color_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";
const rowHover = "hover:bg-soft hover:text-blue-700";
const rowActive = "bg-[var(--neutral-100)] font-semibold text-orange-700";

// Collapsed by default, on request. Nothing is hidden by this: `FilterSection` renders
// the active node's name (and its clear ×) in the *header*, outside the collapsed body,
// so on a class page your position is visible while the list is shut. Note the
// disclosure is uncontrolled (`useState(defaultOpen)` in `FilterSection`), so it resets
// to closed when the section unmounts — including on every category navigation, since
// that remounts the listing.
export default function CategoryFacet({
  title = "Category",
  tree = [],
  activeId = null,
  defaultOpen = false,
}) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const isActive = (id) => activeId != null && areFilterIdsEqual(activeId, id);
  const isOn = (category) =>
    isActive(category.id) || category.children.some((sub) => isActive(sub.id));

  const visibleTree = showAll
    ? tree
    : tree.filter((category, index) => index < CAT_CAP || isOn(category));
  const hiddenCount = tree.length - visibleTree.length;

  const activeName = (() => {
    if (activeId == null) return "";
    for (const category of tree) {
      if (isActive(category.id)) return category.name;
      const sub = category.children.find((child) => isActive(child.id));
      if (sub) return sub.name;
    }
    return "";
  })();

  return (
    <FilterSection
      title={title}
      summary={activeName}
      onClear={activeName ? () => navigate("/products") : undefined}
      clearAriaLabel="Clear category filter"
      defaultOpen={defaultOpen}
      contentClassName="mt-[2px] flex flex-col gap-[2px] px-[8px] pb-[4px]"
    >
      <Link
        to="/products"
        className={`${rowBase} ${activeId == null ? rowActive : `text-ink-2 ${rowHover}`}`}
      >
        <span className="min-w-0 flex-1 truncate">All products</span>
      </Link>

      {visibleTree.map((category) => {
        const on = isOn(category);
        const active = isActive(category.id);

        return (
          <div key={`${category.id}-${category.name}`} className="contents">
            <Link
              to={buildClassificationPath(category)}
              className={`${rowBase} ${
                active
                  ? rowActive
                  : on
                    ? `font-semibold text-ink ${rowHover}`
                    : `text-ink-2 ${rowHover}`
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{category.name}</span>
            </Link>

            {on &&
              category.children.map((sub) => (
                <Link
                  key={`${sub.id}-${sub.name}`}
                  to={buildClassificationPath(sub)}
                  className={`${rowBase} !pl-[24px] ${
                    isActive(sub.id) ? rowActive : `text-ink-2 ${rowHover}`
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{sub.name}</span>
                </Link>
              ))}
          </div>
        );
      })}

      {(hiddenCount > 0 || showAll) && (
        <button
          type="button"
          onClick={() => setShowAll((current) => !current)}
          className="mb-[2px] ml-[8px] mt-[4px] inline-flex cursor-pointer items-center gap-[3px] self-start border-0 bg-transparent p-0 font-sans text-2xs font-semibold text-blue-700 [transition:color_var(--duration-fast)] hover:text-blue-800 focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          <Icon
            name="expand_more"
            size={15}
            weight={400}
            className={`[transition:transform_var(--duration-base)_var(--ease-spring)] ${
              showAll ? "rotate-180" : ""
            }`}
          />
          {showAll ? "Show fewer" : `Show ${hiddenCount} more`}
        </button>
      )}
    </FilterSection>
  );
}
